import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ItemAvaliacao, Nota, RelatorioAvaliacoes as Relatorio } from '@/features/pos-venda/api/avaliacoesApi';
import { usePosVendaEntitlement } from '@/features/pos-venda/hooks/usePosVendaEntitlement';
import type { EstadoRelatorioAvaliacoes } from '@/features/relatorios/hooks/useRelatorioAvaliacoes';
import { ROTAS } from '@/app/rotas';
import { plataforma } from '@/platform';
import { Button } from '@/shared/ui/Button';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Paginacao } from '@/shared/ui/Paginacao';
import { SearchInput } from '@/shared/ui/SearchInput';
import { Toast } from '@/shared/ui/Toast';
import { IconAlert, IconCloud, IconMessage, IconSearch } from '@/shared/ui/Icon';
import { usePaginacao } from '@/shared/hooks/usePaginacao';
import {
  LIMITES_RUIM,
  NOTAS,
  NOTAS_DESC,
  descreverVeiculo,
  destaquesResponsaveis,
  ehRuim,
  formatarDataHora,
  formatarMedia,
  formatarPercentual,
  formatarTelefoneCliente,
  fracao,
  normalizarLimite,
  preenchimentoEstrelas,
  rotuloMes,
  temFiltroAtivo,
  textoCriterio,
} from './avaliacoesRelatorio';
import './RelatorioAvaliacoes.css';

const LIMITE_COMENTARIO = 140;

export function RelatorioAvaliacoes({ estado }: { estado: EstadoRelatorioAvaliacoes }) {
  const { dados, carregando, erro, filtros, recarregar } = estado;
  const posVenda = usePosVendaEntitlement();
  const [toast, setToast] = useState<string | null>(null);

  const filtroAtivo = temFiltroAtivo(filtros);
  const semNada =
    dados != null &&
    dados.resumo.totalRespostas === 0 &&
    dados.resumo.linksGerados === 0 &&
    !filtroAtivo;
  const posVendaInativo = !posVenda.carregando && !posVenda.ativo;

  return (
    <div className="aval">
      <Toast mensagem={toast} onFechar={() => setToast(null)} />

      {erro && <AvisoErro semConexao={erro.semConexao} mensagem={erro.mensagem} onTentar={recarregar} />}

      {!dados && carregando && <p className="hint">Carregando avaliações…</p>}

      {dados && semNada && posVendaInativo ? (
        <ConvitePosVenda />
      ) : (
        dados && (
          <>
            {posVendaInativo && (
              <p className="alert alert--aviso" role="status">
                O pós-venda está inativo: os dados abaixo continuam disponíveis, mas nenhum novo
                pedido de avaliação será enviado até reativar.
              </p>
            )}
            <Indicadores dados={dados} />
            <CriterioRuim
              limite={normalizarLimite(dados.limiteRuim)}
              salvando={estado.salvandoLimite}
              onMudar={async (n) => {
                const falha = await estado.salvarLimite(n);
                if (falha) setToast(`Não foi possível salvar o critério: ${falha}`);
              }}
            />
            <div className="aval-grade">
              <Distribuicao
                dados={dados}
                notasFiltradas={filtros.notas}
                onFiltrarNota={(n) =>
                  estado.alterarFiltros({
                    notas: filtros.notas.length === 1 && filtros.notas[0] === n ? [] : [n],
                  })
                }
              />
              <PorResponsavel
                dados={dados}
                responsavelFiltrado={filtros.responsavelId}
                onFiltrar={(id) =>
                  estado.alterarFiltros({ responsavelId: filtros.responsavelId === id ? '' : id })
                }
              />
            </div>
            <EvolucaoMensal porMes={dados.porMes} />
            <TabelaAvaliacoes estado={estado} dados={dados} />
          </>
        )
      )}
    </div>
  );
}

// ---------------------------------------------------------------------

export function Estrelas({ valor, tamanho }: { valor: number | null; tamanho?: 'lg' }) {
  const partes = preenchimentoEstrelas(valor);
  return (
    <span
      className={`aval-estrelas${tamanho === 'lg' ? ' aval-estrelas--lg' : ''}`}
      role="img"
      aria-label={valor == null ? 'Sem nota' : `${formatarMedia(valor)} de 5 estrelas`}
    >
      {partes.map((p, i) => (
        <span key={i} className="aval-estrela" aria-hidden>
          <span className="aval-estrela__cheia" style={{ width: `${p * 100}%` }}>
            ★
          </span>
          ★
        </span>
      ))}
    </span>
  );
}

function AvisoErro({
  semConexao,
  mensagem,
  onTentar,
}: {
  semConexao: boolean;
  mensagem: string;
  onTentar: () => void;
}) {
  const texto = semConexao
    ? plataforma.capacidades.offline
      ? 'Sem conexão com a internet. As avaliações ficam no servidor (o cliente responde por um link online), então este relatório só funciona conectado.'
      : 'Sem conexão com o servidor. Verifique a internet e tente novamente.'
    : `Falha ao carregar avaliações: ${mensagem}`;
  return (
    <div className="alert alert--error" role="alert" style={{ flexWrap: 'wrap' }}>
      {semConexao ? <IconCloud aria-hidden /> : <IconAlert aria-hidden />}
      <span style={{ flex: '1 1 240px' }}>{texto}</span>
      <Button onClick={onTentar}>Tentar novamente</Button>
    </div>
  );
}

function ConvitePosVenda() {
  const navigate = useNavigate();
  return (
    <div className="card">
      <div className="card__body aval-convite">
        <span className="empty-state__icon">
          <IconMessage aria-hidden />
        </span>
        <p className="empty-state__title">Descubra o que seus clientes acham do serviço</p>
        <p>
          Com o Pós-venda ativo, o cliente recebe pelo WhatsApp um link para dar de 1 a 5
          estrelas e deixar um comentário. As respostas aparecem aqui, com média, notas ruins e
          desempenho por responsável.
        </p>
        <Button variante="primary" onClick={() => navigate(ROTAS.posVenda)}>
          Conhecer o Pós-venda
        </Button>
      </div>
    </div>
  );
}

function Kpi(props: {
  rotulo: string;
  valor: React.ReactNode;
  detalhe?: React.ReactNode;
  tom?: 'success' | 'danger';
}) {
  return (
    <div className="stat-card">
      <span className="stat-card__label">{props.rotulo}</span>
      <span className="aval-kpi__valor-linha">
        <span className={`stat-card__value${props.tom ? ` stat-card__value--${props.tom}` : ''}`}>
          {props.valor}
        </span>
      </span>
      {props.detalhe != null && <span className="aval-kpi__detalhe">{props.detalhe}</span>}
    </div>
  );
}

function Indicadores({ dados }: { dados: Relatorio }) {
  const r = dados.resumo;
  const total = r.totalRespostas;
  const pct = (n: number) => formatarPercentual(fracao(n, total));
  return (
    <div className="aval-kpis">
      <Kpi
        rotulo="Média de aprovação"
        valor={formatarMedia(r.media)}
        detalhe={<Estrelas valor={r.media} tamanho="lg" />}
      />
      <Kpi rotulo="Total de avaliações" valor={total.toLocaleString('pt-BR')} detalhe="respostas no período" />
      <Kpi rotulo="Avaliaram bem" valor={r.boas.toLocaleString('pt-BR')} detalhe={`${pct(r.boas)} das respostas`} tom="success" />
      <Kpi rotulo="Avaliaram mal" valor={r.ruins.toLocaleString('pt-BR')} detalhe={`${pct(r.ruins)} das respostas`} tom="danger" />
      <Kpi
        rotulo="Taxa de resposta"
        valor={formatarPercentual(r.taxaResposta)}
        detalhe={`${total.toLocaleString('pt-BR')} de ${r.linksGerados.toLocaleString('pt-BR')} links enviados`}
      />
      <Kpi rotulo="Com comentário" valor={r.comComentario.toLocaleString('pt-BR')} detalhe={`${pct(r.comComentario)} das respostas`} />
    </div>
  );
}

function CriterioRuim({
  limite,
  salvando,
  onMudar,
}: {
  limite: number;
  salvando: boolean;
  onMudar: (n: number) => void | Promise<void>;
}) {
  return (
    <div className="card">
      <div className="card__body aval-criterio">
        <span className="aval-criterio__rotulo" id="aval-criterio-rotulo">
          Considerar avaliação ruim abaixo de
        </span>
        <div className="segmented" role="radiogroup" aria-labelledby="aval-criterio-rotulo">
          {LIMITES_RUIM.map((n) => (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={limite === n}
              disabled={salvando}
              className={`segmented__item${limite === n ? ' segmented__item--ativo' : ''}`}
              onClick={() => {
                if (n !== limite) void onMudar(n);
              }}
            >
              {n} ★
            </button>
          ))}
        </div>
        <span className="aval-criterio__rotulo">estrelas</span>
        <span className="aval-criterio__texto">
          {salvando ? 'Salvando…' : `${textoCriterio(limite)}. Vale para os indicadores, a tabela e a exportação.`}
        </span>
      </div>
    </div>
  );
}

function Distribuicao({
  dados,
  notasFiltradas,
  onFiltrarNota,
}: {
  dados: Relatorio;
  notasFiltradas: Nota[];
  onFiltrarNota: (n: Nota) => void;
}) {
  const total = dados.resumo.totalRespostas;
  const limite = normalizarLimite(dados.limiteRuim);
  const maior = Math.max(1, ...NOTAS.map((n) => dados.resumo.distribuicao[`${n}`] ?? 0));
  return (
    <div className="card">
      <div className="card__header">
        <span className="card__title">Distribuição por nota</span>
      </div>
      <div className="card__body">
        <div className="aval-barras">
          {NOTAS_DESC.map((n) => {
            const qtd = dados.resumo.distribuicao[`${n}`] ?? 0;
            const ativa = notasFiltradas.length === 1 && notasFiltradas[0] === n;
            return (
              <button
                key={n}
                type="button"
                className={`aval-barra${ativa ? ' aval-barra--ativa' : ''}`}
                aria-pressed={ativa}
                title={ativa ? 'Remover filtro desta nota' : `Mostrar só avaliações com nota ${n}`}
                onClick={() => onFiltrarNota(n)}
              >
                <span className="aval-barra__rotulo">{n} ★</span>
                <span className="aval-barra__trilho" aria-hidden>
                  <span
                    className={`aval-barra__preench${ehRuim(n, limite) ? ' aval-barra__preench--ruim' : ''}`}
                    style={{ width: `${(qtd / maior) * 100}%`, display: 'block' }}
                  />
                </span>
                <span className="aval-barra__valor">
                  {qtd.toLocaleString('pt-BR')} · {formatarPercentual(fracao(qtd, total))}
                </span>
              </button>
            );
          })}
        </div>
        <p className="hint" style={{ marginTop: 8 }}>
          Toque numa barra para filtrar a tabela por aquela nota.
        </p>
      </div>
    </div>
  );
}

function PorResponsavel({
  dados,
  responsavelFiltrado,
  onFiltrar,
}: {
  dados: Relatorio;
  responsavelFiltrado: string;
  onFiltrar: (id: string) => void;
}) {
  const { melhor, pior } = destaquesResponsaveis(dados.porResponsavel);
  const chave = (r: Relatorio['porResponsavel'][number]) => r.responsavelId ?? `sem:${r.nome}`;
  return (
    <div className="card">
      <div className="card__header">
        <span className="card__title">Por responsável</span>
      </div>
      <div className="card__body card__body--flush">
        {dados.porResponsavel.length === 0 ? (
          <p className="empty-state">Nenhuma avaliação com responsável no período.</p>
        ) : (
          <div className="table-scroll">
            <table className="table table--responsive">
              <thead>
                <tr>
                  <th>Responsável</th>
                  <th className="table__numeric">Avaliações</th>
                  <th className="table__numeric">Média</th>
                </tr>
              </thead>
              <tbody>
                {dados.porResponsavel.map((r) => {
                  const k = chave(r);
                  const destaque = k === melhor ? 'melhor' : k === pior ? 'pior' : null;
                  return (
                    <tr key={k} className={destaque ? `aval-resp--${destaque}` : undefined}>
                      <td className="table__primary" data-label="">
                        <span className="aval-resp__nome">
                          {r.responsavelId ? (
                            <button
                              type="button"
                              className="aval-link-os"
                              aria-pressed={responsavelFiltrado === r.responsavelId}
                              title="Filtrar a tabela por este responsável"
                              onClick={() => onFiltrar(r.responsavelId as string)}
                            >
                              {r.nome}
                            </button>
                          ) : (
                            r.nome
                          )}
                          {destaque === 'melhor' && <span className="badge badge--ativo">Melhor média</span>}
                          {destaque === 'pior' && <span className="badge badge--saida">Pior média</span>}
                        </span>
                      </td>
                      <td className="table__numeric" data-label="Avaliações">
                        {r.quantidade.toLocaleString('pt-BR')}
                      </td>
                      <td className="table__numeric" data-label="Média">
                        <span className="aval-nota">
                          <span className="aval-nota__numero">{formatarMedia(r.media)}</span>
                          <Estrelas valor={r.media} />
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function EvolucaoMensal({ porMes }: { porMes: Relatorio['porMes'] }) {
  if (porMes.length === 0) return null;
  const maior = Math.max(1, ...porMes.map((m) => m.quantidade));
  return (
    <div className="card">
      <div className="card__header">
        <span className="card__title">Evolução mensal</span>
      </div>
      <div className="card__body">
        <div className="aval-barras">
          {porMes.map((m) => (
            <div key={m.mes} className="aval-barra aval-barra--mes aval-barra--estatica">
              <span className="aval-barra__rotulo">{rotuloMes(m.mes)}</span>
              <span className="aval-barra__trilho" aria-hidden>
                <span
                  className="aval-barra__preench aval-barra__preench--neutra"
                  style={{ width: `${(m.quantidade / maior) * 100}%`, display: 'block' }}
                />
              </span>
              <span className="aval-barra__valor">
                {m.quantidade.toLocaleString('pt-BR')} · média {formatarMedia(m.media)} ★
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function FiltrosTabela({ estado }: { estado: EstadoRelatorioAvaliacoes }) {
  const { filtros, alterarFiltros, limparFiltros, responsaveis } = estado;
  const alternarNota = (n: Nota) =>
    alterarFiltros({
      notas: filtros.notas.includes(n)
        ? filtros.notas.filter((x) => x !== n)
        : [...filtros.notas, n].sort((a, b) => a - b),
    });
  const responsavelForaDaLista =
    filtros.responsavelId !== '' && !responsaveis.some((r) => r.id === filtros.responsavelId);
  return (
    <div className="aval-filtros">
      <div className="aval-notas" role="group" aria-label="Filtrar por nota">
        {NOTAS.map((n) => (
          <button
            key={n}
            type="button"
            className="aval-chip"
            aria-pressed={filtros.notas.includes(n)}
            onClick={() => alternarNota(n)}
          >
            {n} ★
          </button>
        ))}
      </div>
      <div className="segmented aval-classificacao" role="radiogroup" aria-label="Classificação">
        {(
          [
            ['todas', 'Todas'],
            ['boas', 'Boas'],
            ['ruins', 'Ruins'],
          ] as const
        ).map(([v, rotulo]) => (
          <button
            key={v}
            type="button"
            role="radio"
            aria-checked={filtros.classificacao === v}
            className={`segmented__item${filtros.classificacao === v ? ' segmented__item--ativo' : ''}`}
            onClick={() => alterarFiltros({ classificacao: v })}
          >
            {rotulo}
          </button>
        ))}
      </div>
      <label className="checkbox">
        <input
          type="checkbox"
          checked={filtros.comComentario}
          onChange={(e) => alterarFiltros({ comComentario: e.target.checked })}
        />
        Só com comentário
      </label>
      <select
        aria-label="Filtrar por responsável"
        className="input"
        value={filtros.responsavelId}
        onChange={(e) => alterarFiltros({ responsavelId: e.target.value })}
      >
        <option value="">Todos os responsáveis</option>
        {responsavelForaDaLista && <option value={filtros.responsavelId}>(responsável selecionado)</option>}
        {responsaveis.map((r) => (
          <option key={r.id} value={r.id}>
            {r.nome}
          </option>
        ))}
      </select>
      <SearchInput
        valor={filtros.busca}
        onChange={(v) => alterarFiltros({ busca: v })}
        placeholder="Buscar cliente, nº da O.S., placa ou comentário…"
        ariaLabel="Buscar avaliações"
      />
      {temFiltroAtivo(filtros) && (
        <Button variante="ghost" onClick={limparFiltros}>
          Limpar filtros
        </Button>
      )}
    </div>
  );
}

function Comentario({ texto }: { texto: string | null }) {
  const [aberto, setAberto] = useState(false);
  const t = texto?.trim();
  if (!t) return <span className="table__muted">—</span>;
  const longo = t.length > LIMITE_COMENTARIO;
  return (
    <span className="aval-comentario">
      {longo && !aberto ? `${t.slice(0, LIMITE_COMENTARIO).trimEnd()}…` : t}
      {longo && (
        <button
          type="button"
          className="aval-comentario__mais"
          aria-expanded={aberto}
          onClick={() => setAberto((a) => !a)}
        >
          {aberto ? 'ver menos' : 'ver mais'}
        </button>
      )}
    </span>
  );
}

function NotaItem({ nota, limite }: { nota: Nota; limite: number }) {
  const ruim = ehRuim(nota, limite);
  return (
    <span className={`aval-nota ${ruim ? 'aval-nota--ruim' : 'aval-nota--boa'}`}>
      <Estrelas valor={nota} />
      <span className="aval-nota__numero">{nota}</span>
      <span className="sr-only">{ruim ? '(ruim)' : '(boa)'}</span>
    </span>
  );
}

function TabelaAvaliacoes({ estado, dados }: { estado: EstadoRelatorioAvaliacoes; dados: Relatorio }) {
  const navigate = useNavigate();
  const itens = dados.itens;
  const pag = usePaginacao(itens, 25);
  const limite = normalizarLimite(dados.limiteRuim);
  const filtroAtivo = temFiltroAtivo(estado.filtros);

  const abrirOS = (i: ItemAvaliacao) => navigate(ROTAS.ordem(i.orcamentoId));

  return (
    <div className="card">
      <div className="card__header">
        <span className="card__title">
          Avaliações ({itens.length.toLocaleString('pt-BR')}
          {filtroAtivo ? ` de ${dados.resumo.totalRespostas.toLocaleString('pt-BR')}` : ''})
        </span>
        {estado.carregando && <span className="hint">Atualizando…</span>}
      </div>
      <FiltrosTabela estado={estado} />
      <div className={`card__body card__body--flush${estado.carregando ? ' aval-carregando' : ''}`}>
        {itens.length === 0 ? (
          filtroAtivo ? (
            <div>
              <EmptyState
                icone={<IconSearch />}
                titulo="Nenhuma avaliação com esses filtros."
                descricao="Ajuste ou limpe os filtros para ver as demais avaliações do período."
              />
              <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 24 }}>
                <Button onClick={estado.limparFiltros}>Limpar filtros</Button>
              </div>
            </div>
          ) : (
            <EmptyState
              icone={<IconMessage />}
              titulo="Nenhuma avaliação no período."
              descricao="Ative o agendado de avaliação no Pós-venda com a variável {{avaliacao}} na mensagem — o cliente recebe o link e a resposta aparece aqui."
            />
          )
        ) : (
          <>
            <div className="table-scroll">
              <table className="table table--responsive">
                <thead>
                  <tr>
                    <th>Resposta</th>
                    <th>Cliente</th>
                    <th>O.S.</th>
                    <th>Veículo</th>
                    <th>Responsável</th>
                    <th>Nota</th>
                    <th>Comentário</th>
                  </tr>
                </thead>
                <tbody>
                  {pag.paginados.map((i) => {
                    const tel = formatarTelefoneCliente(i.clienteTelefone);
                    return (
                      <tr key={i.id}>
                        <td className="table__muted" data-label="Resposta">
                          {formatarDataHora(i.respondidoEm)}
                        </td>
                        <td data-label="Cliente">
                          <span className="aval-cliente">
                            <span className="table__primary">{i.clienteNome ?? '—'}</span>
                            {tel && <span className="table__muted">{tel}</span>}
                          </span>
                        </td>
                        <td data-label="O.S.">
                          <button
                            type="button"
                            className="aval-link-os"
                            title="Abrir a ordem de serviço"
                            onClick={() => abrirOS(i)}
                          >
                            {i.orcamentoNumero ?? 'Abrir O.S.'}
                          </button>
                        </td>
                        <td className="table__muted" data-label="Veículo">
                          {descreverVeiculo(i)}
                        </td>
                        <td data-label="Responsável">{i.responsavelNome ?? '—'}</td>
                        <td data-label="Nota">
                          <NotaItem nota={i.nota} limite={limite} />
                        </td>
                        <td data-label="Comentário">
                          <Comentario texto={i.comentario} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Paginacao
              pagina={pag.pagina}
              totalPaginas={pag.totalPaginas}
              total={pag.total}
              tamanhoPagina={pag.tamanhoPagina}
              onMudarPagina={pag.setPagina}
            />
          </>
        )}
      </div>
    </div>
  );
}
